import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import {
  buildAttributesFilterTypes,
  CONTACT_ATTRIBUTES,
} from './helper/filterHelper.js';
import countries from 'shared/constants/countries.js';

/**
 * @typedef {Object} FilterOption
 * @property {string|number} id
 * @property {string} name
 * @property {import('vue').VNode} [icon]
 */

/**
 * @typedef {Object} FilterOperator
 * @property {string} value
 * @property {string} label
 * @property {string} icon
 * @property {boolean} hasInput
 */

/**
 * @typedef {Object} FilterType
 * @property {string} attributeKey - The attribute key
 * @property {string} value - This is a proxy for the attribute key used in FilterSelect
 * @property {string} attributeName - The attribute name used to display on the UI
 * @property {string} label - This is a proxy for the attribute name used in FilterSelect
 * @property {'multiSelect'|'searchSelect'|'plainText'|'date'|'booleanSelect'} inputType - The input type for the attribute
 * @property {FilterOption[]} [options] - The options available for the attribute if it is a multiSelect or singleSelect type
 * @property {'text'|'number'} dataType
 * @property {FilterOperator[]} filterOperators - The operators available for the attribute
 * @property {'standard'|'additional'|'customAttributes'} attributeModel
 */

/**
 * @typedef {Object} FilterGroup
 * @property {string} name
 * @property {FilterType[]} attributes
 */

/**
 * Composable that provides conversation filtering context
 * @returns {{ filterTypes: import('vue').ComputedRef<FilterType[]>, filterGroups: import('vue').ComputedRef<FilterGroup[]> }}
 */
export function useContactFilterContext() {
  const { t } = useI18n();

  const contactAttributes = useMapGetter('attributes/getContactAttributes');
  const labels = useMapGetter('labels/getLabels');

  const {
    equalityOperators,
    containmentOperators,
    comparisonOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  // Filtros de navegação/pixel só suportam contains/does_not_contain (ver filter_keys.yml).
  const trackingOperators = computed(() =>
    containmentOperators.value.filter(op =>
      ['contains', 'does_not_contain'].includes(op.value)
    )
  );

  // Filtros de navegação (pixel). inputType 'trackingWindow' = termo (opcional) + janela em dias.
  // requiresTerm: false → só a janela em dias (viewed_cart, started_checkout).
  const trackingFilter = (
    attributeKey,
    attributeName,
    requiresTerm = true
  ) => ({
    attributeKey,
    value: attributeKey,
    attributeName,
    label: attributeName,
    inputType: 'trackingWindow',
    requiresTerm,
    dataType: 'text',
    filterOperators: trackingOperators.value,
    attributeModel: 'standard',
  });

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(
      contactAttributes.value,
      getOperatorTypes,
      'contact'
    )
  );

  /**
   * @type {import('vue').ComputedRef<FilterType[]>}
   */
  const filterTypes = computed(() => [
    {
      attributeKey: CONTACT_ATTRIBUTES.NAME,
      value: CONTACT_ATTRIBUTES.NAME,
      attributeName: t('CONTACTS_LAYOUT.FILTER.NAME'),
      label: t('CONTACTS_LAYOUT.FILTER.NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.EMAIL,
      value: CONTACT_ATTRIBUTES.EMAIL,
      attributeName: t('CONTACTS_LAYOUT.FILTER.EMAIL'),
      label: t('CONTACTS_LAYOUT.FILTER.EMAIL'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.PHONE_NUMBER,
      value: CONTACT_ATTRIBUTES.PHONE_NUMBER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.PHONE_NUMBER'),
      label: t('CONTACTS_LAYOUT.FILTER.PHONE_NUMBER'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.IDENTIFIER,
      value: CONTACT_ATTRIBUTES.IDENTIFIER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.IDENTIFIER'),
      label: t('CONTACTS_LAYOUT.FILTER.IDENTIFIER'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.COUNTRY_CODE,
      value: CONTACT_ATTRIBUTES.COUNTRY_CODE,
      attributeName: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      label: t('FILTER.ATTRIBUTES.COUNTRY_NAME'),
      inputType: 'searchSelect',
      options: countries,
      dataType: 'number',
      filterOperators: equalityOperators.value,
      attributeModel: 'additional',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.CITY,
      value: CONTACT_ATTRIBUTES.CITY,
      attributeName: t('CONTACTS_LAYOUT.FILTER.CITY'),
      label: t('CONTACTS_LAYOUT.FILTER.CITY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.CREATED_AT,
      value: CONTACT_ATTRIBUTES.CREATED_AT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
      label: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LAST_ACTIVITY_AT,
      value: CONTACT_ATTRIBUTES.LAST_ACTIVITY_AT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      label: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.REFERER,
      value: CONTACT_ATTRIBUTES.REFERER,
      attributeName: t('CONTACTS_LAYOUT.FILTER.REFERER_LINK'),
      label: t('CONTACTS_LAYOUT.FILTER.REFERER_LINK'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.BLOCKED,
      value: CONTACT_ATTRIBUTES.BLOCKED,
      attributeName: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
      label: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
      inputType: 'searchSelect',
      options: [
        {
          id: 'true',
          name: t('CONTACTS_LAYOUT.FILTER.BLOCKED_TRUE'),
        },
        {
          id: 'false',
          name: t('CONTACTS_LAYOUT.FILTER.BLOCKED_FALSE'),
        },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LABELS,
      value: CONTACT_ATTRIBUTES.LABELS,
      attributeName: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
      label: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
      inputType: 'multiSelect',
      options: labels.value?.map(label => ({
        id: label.title,
        name: label.title,
      })),
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.ORDERS_COUNT,
      value: CONTACT_ATTRIBUTES.ORDERS_COUNT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.ORDERS_COUNT'),
      label: t('CONTACTS_LAYOUT.FILTER.ORDERS_COUNT'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.TOTAL_SPENT,
      value: CONTACT_ATTRIBUTES.TOTAL_SPENT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.TOTAL_SPENT'),
      label: t('CONTACTS_LAYOUT.FILTER.TOTAL_SPENT'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.LAST_ORDER_AT,
      value: CONTACT_ATTRIBUTES.LAST_ORDER_AT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.LAST_ORDER_AT'),
      label: t('CONTACTS_LAYOUT.FILTER.LAST_ORDER_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.FINANCIAL_STATUS,
      value: CONTACT_ATTRIBUTES.FINANCIAL_STATUS,
      attributeName: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_STATUS'),
      label: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_STATUS'),
      inputType: 'searchSelect',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
      options: [
        {
          id: 'pending',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.PENDING'),
        },
        {
          id: 'authorized',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.AUTHORIZED'),
        },
        {
          id: 'paid',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.PAID'),
        },
        {
          id: 'partially_paid',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.PARTIALLY_PAID'),
        },
        {
          id: 'refunded',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.REFUNDED'),
        },
        {
          id: 'partially_refunded',
          name: t(
            'CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.PARTIALLY_REFUNDED'
          ),
        },
        {
          id: 'cancelled',
          name: t('CONTACTS_LAYOUT.FILTER.FINANCIAL_OPTIONS.CANCELLED'),
        },
      ],
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.FULFILLMENT_STATUS,
      value: CONTACT_ATTRIBUTES.FULFILLMENT_STATUS,
      attributeName: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_STATUS'),
      label: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_STATUS'),
      inputType: 'searchSelect',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
      options: [
        {
          id: 'unfulfilled',
          name: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_OPTIONS.UNFULFILLED'),
        },
        {
          id: 'partially_shipped',
          name: t(
            'CONTACTS_LAYOUT.FILTER.FULFILLMENT_OPTIONS.PARTIALLY_SHIPPED'
          ),
        },
        {
          id: 'shipped',
          name: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_OPTIONS.SHIPPED'),
        },
        {
          id: 'delivered',
          name: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_OPTIONS.DELIVERED'),
        },
        {
          id: 'returned',
          name: t('CONTACTS_LAYOUT.FILTER.FULFILLMENT_OPTIONS.RETURNED'),
        },
      ],
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.BOUGHT_PRODUCT,
      value: CONTACT_ATTRIBUTES.BOUGHT_PRODUCT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.BOUGHT_PRODUCT'),
      label: t('CONTACTS_LAYOUT.FILTER.BOUGHT_PRODUCT'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: CONTACT_ATTRIBUTES.RFM_SEGMENT,
      value: CONTACT_ATTRIBUTES.RFM_SEGMENT,
      attributeName: t('CONTACTS_LAYOUT.FILTER.RFM_SEGMENT'),
      label: t('CONTACTS_LAYOUT.FILTER.RFM_SEGMENT'),
      inputType: 'multiSelect',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
      options: [
        {
          id: 'champions',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.CHAMPIONS'),
        },
        { id: 'loyal', name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.LOYAL') },
        {
          id: 'potential_loyalists',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.POTENTIAL_LOYALISTS'),
        },
        {
          id: 'new_customers',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.NEW_CUSTOMERS'),
        },
        {
          id: 'promising',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.PROMISING'),
        },
        {
          id: 'need_attention',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.NEED_ATTENTION'),
        },
        {
          id: 'at_risk',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.AT_RISK'),
        },
        {
          id: 'cant_lose',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.CANT_LOSE'),
        },
        {
          id: 'hibernating',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.HIBERNATING'),
        },
        { id: 'lost', name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.LOST') },
        {
          id: 'prospects',
          name: t('CONTACTS_LAYOUT.FILTER.RFM_OPTIONS.PROSPECTS'),
        },
      ],
    },
    trackingFilter(
      CONTACT_ATTRIBUTES.VIEWED_PAGE,
      t('CONTACTS_LAYOUT.FILTER.VIEWED_PAGE')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.VIEWED_PRODUCT,
      t('CONTACTS_LAYOUT.FILTER.VIEWED_PRODUCT')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.VIEWED_COLLECTION,
      t('CONTACTS_LAYOUT.FILTER.VIEWED_COLLECTION')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.SEARCHED_TERM,
      t('CONTACTS_LAYOUT.FILTER.SEARCHED_TERM')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.ADDED_TO_CART,
      t('CONTACTS_LAYOUT.FILTER.ADDED_TO_CART')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.REMOVED_FROM_CART,
      t('CONTACTS_LAYOUT.FILTER.REMOVED_FROM_CART')
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.VIEWED_CART,
      t('CONTACTS_LAYOUT.FILTER.VIEWED_CART'),
      false
    ),
    trackingFilter(
      CONTACT_ATTRIBUTES.STARTED_CHECKOUT,
      t('CONTACTS_LAYOUT.FILTER.STARTED_CHECKOUT'),
      false
    ),
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
